import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  type CrossReferenceOccurrence,
  crossReferenceResponseSchema,
  type CrossReferenceState,
} from 'features/knowledgeDocumentViewer/utils/crossReference'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

export function useCrossReference(
  siteId: string,
  documentId: string,
  page: number,
  active: boolean,
  userId?: string
) {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const { selectedPipelineConfigId, isPreviewMode } = usePipelineConfig()
  const [state, setState] = useState<CrossReferenceState>()
  const controller = useRef<AbortController>()
  const cache = useRef(new Map<string, CrossReferenceState>())
  const close = useCallback(() => {
    controller.current?.abort()
    controller.current = undefined
    setState(undefined)
  }, [])
  useLayoutEffect(() => {
    cache.current.clear()
    close()
    return close
  }, [
    siteId,
    documentId,
    page,
    active,
    userId,
    selectedPipelineConfigId,
    isPreviewMode,
    axios,
    POWER_BACKEND_AI_URL,
    close,
  ])
  const search = useCallback(
    async (occurrence: CrossReferenceOccurrence) => {
      if (!active || !selectedPipelineConfigId || !userId) return
      controller.current?.abort()
      controller.current = undefined
      const key = JSON.stringify([
        occurrence.source_page_number,
        occurrence.point,
        occurrence.candidate_index ?? 'automatic',
      ])
      const cached = cache.current.get(key)
      if (cached) {
        setState({ ...cached, occurrence })
        return
      }
      const current = new AbortController()
      controller.current = current
      setState((previous) => ({
        occurrence,
        loading: true,
        sourceBBox: occurrence.source_bbox,
        candidates:
          occurrence.candidate_index !== undefined
            ? previous?.candidates
            : undefined,
        selectedCandidate: occurrence.candidate_index ?? 0,
      }))
      try {
        const response = await axios.post(
          `${POWER_BACKEND_AI_URL}/api/v2/documents/cross-reference`,
          {
            ...occurrence,
            selected_text: occurrence.selected_text.slice(0, 500),
            site_id: siteId,
            source_document_id: documentId,
            pipeline_config_id: selectedPipelineConfigId,
            preview_mode: isPreviewMode,
          },
          { signal: current.signal, timeout: 30000 }
        )
        const data = crossReferenceResponseSchema.parse(response.data)
        if (current.signal.aborted || controller.current !== current) return
        if (data.pipeline_config_id !== selectedPipelineConfigId)
          throw new Error('Document set changed')
        const resolved: CrossReferenceState = {
          occurrence,
          loading: false,
          query: data.query,
          candidates: data.candidates,
          selectedCandidate: data.selected_candidate,
          emptyRegion:
            data.status === 'empty_region' || data.status === 'unclear',
          sourceBBox: data.source_bbox ?? occurrence.source_bbox,
          documents: data.documents.filter((d) => d.document_id !== documentId),
        }
        if (cache.current.size >= 30) {
          const oldest = cache.current.keys().next().value
          if (oldest !== undefined) cache.current.delete(oldest)
        }
        cache.current.set(key, resolved)
        setState(resolved)
      } catch {
        if (!current.signal.aborted && controller.current === current)
          setState((previous) => ({
            ...previous,
            occurrence,
            loading: false,
            error: t`Could not find related documents. Try again.`,
          }))
      }
    },
    [
      active,
      userId,
      selectedPipelineConfigId,
      isPreviewMode,
      axios,
      POWER_BACKEND_AI_URL,
      siteId,
      documentId,
    ]
  )
  const selectCandidate = useCallback(
    (index: number) => {
      const candidate = state?.candidates?.[index]
      if (!state || !candidate) return
      void search({
        ...state.occurrence,
        candidate_index: index,
        selected_text: candidate.text,
        source_bbox: candidate.bbox,
      })
    },
    [state, search]
  )
  return { state, search, close, selectCandidate }
}
